import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Package, Plus, Search, ArrowLeft, Trash2, AlertTriangle, 
  Box, Sofa, Bed, ChefHat, Monitor, Music, Car, Check, X, Briefcase
} from 'lucide-react';
import api, { inventoryAPI } from '../utils/api';

const categoryIcons = {
  'Boxes': Box,
  'Living Room': Sofa,
  'Bedroom': Bed,
  'Kitchen': ChefHat,
  'Electronics': Monitor,
  'Musical Instruments': Music,
  'Special': Car,
  'default': Package
};

const InventoryManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({ domestic: [], commercial: [] });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [inventoryType, setInventoryType] = useState('domestic');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  // Job selector state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (jobId) {
      fetchData();
    }
  }, [inventoryType, jobId]);

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await api.get('/jobs');
      setJobs(response.data || []);
    } catch (error) {
      console.error('Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchData = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        api.get(`/inventory/templates?item_type=${inventoryType}`),
        api.get('/inventory/categories')
      ]);
      setTemplates(templatesRes.data.items);
      setCategories(categoriesRes.data);
      
      const itemsRes = await api.get(`/jobs/${jobId}/inventory`);
      setInventoryItems(itemsRes.data || []);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectJob = (selectedJobId) => {
    setSearchParams({ jobId: selectedJobId });
  };

  const filteredTemplates = templates.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedTemplates = filteredTemplates.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const handleAddItem = async () => {
    if (!jobId) {
      toast.error('Please select a job first');
      return;
    }

    try {
      await api.post('/inventory', {
        job_id: jobId,
        category: selectedItem.category,
        item_name: selectedItem.name,
        quantity: quantity,
        is_fragile: selectedItem.is_fragile,
        notes: selectedItem.notes
      });
      
      toast.success(`Added ${quantity}x ${selectedItem.name}`);
      setShowAddDialog(false);
      setSelectedItem(null);
      setQuantity(1);
      
      // Refresh inventory
      const itemsRes = await api.get(`/jobs/${jobId}/inventory`);
      setInventoryItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/inventory/${itemId}`);
      toast.success('Item removed');
      setInventoryItems(inventoryItems.filter(i => i.id !== itemId));
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const openAddDialog = (item) => {
    setSelectedItem(item);
    setQuantity(1);
    setShowAddDialog(true);
  };

  // Quick preset definitions for common bedroom setups
  const presets = {
    '1-bed': [
      { name: 'Double Bed', quantity: 1 },
      { name: 'Wardrobe', quantity: 1 },
      { name: 'Chest of Drawers', quantity: 1 },
      { name: 'Sofa (2 Seater)', quantity: 1 },
      { name: 'TV Stand', quantity: 1 },
      { name: 'Dining Table', quantity: 1 },
      { name: 'Dining Chair', quantity: 2 },
      { name: 'Medium Box', quantity: 10 },
      { name: 'Small Box', quantity: 5 },
    ],
    '2-bed': [
      { name: 'Double Bed', quantity: 1 },
      { name: 'Single Bed', quantity: 1 },
      { name: 'Wardrobe', quantity: 2 },
      { name: 'Chest of Drawers', quantity: 2 },
      { name: 'Sofa (3 Seater)', quantity: 1 },
      { name: 'TV Stand', quantity: 1 },
      { name: 'Dining Table', quantity: 1 },
      { name: 'Dining Chair', quantity: 4 },
      { name: 'Medium Box', quantity: 20 },
      { name: 'Small Box', quantity: 10 },
    ],
    '3-bed': [
      { name: 'Double Bed', quantity: 2 },
      { name: 'Single Bed', quantity: 1 },
      { name: 'Wardrobe', quantity: 3 },
      { name: 'Chest of Drawers', quantity: 3 },
      { name: 'Sofa (3 Seater)', quantity: 1 },
      { name: 'Armchair', quantity: 2 },
      { name: 'TV Stand', quantity: 2 },
      { name: 'Dining Table', quantity: 1 },
      { name: 'Dining Chair', quantity: 6 },
      { name: 'Desk', quantity: 1 },
      { name: 'Large Box', quantity: 10 },
      { name: 'Medium Box', quantity: 25 },
      { name: 'Small Box', quantity: 15 },
    ],
    '4-bed': [
      { name: 'Double Bed', quantity: 2 },
      { name: 'Single Bed', quantity: 2 },
      { name: 'Wardrobe', quantity: 4 },
      { name: 'Chest of Drawers', quantity: 4 },
      { name: 'Sofa (3 Seater)', quantity: 1 },
      { name: 'Sofa (2 Seater)', quantity: 1 },
      { name: 'Armchair', quantity: 2 },
      { name: 'TV Stand', quantity: 2 },
      { name: 'Dining Table', quantity: 1 },
      { name: 'Dining Chair', quantity: 8 },
      { name: 'Desk', quantity: 2 },
      { name: 'Large Box', quantity: 15 },
      { name: 'Medium Box', quantity: 35 },
      { name: 'Small Box', quantity: 20 },
    ],
    '5-bed': [
      { name: 'Double Bed', quantity: 3 },
      { name: 'Single Bed', quantity: 2 },
      { name: 'Wardrobe', quantity: 5 },
      { name: 'Chest of Drawers', quantity: 5 },
      { name: 'Sofa (3 Seater)', quantity: 2 },
      { name: 'Armchair', quantity: 3 },
      { name: 'TV Stand', quantity: 3 },
      { name: 'Dining Table', quantity: 1 },
      { name: 'Dining Chair', quantity: 10 },
      { name: 'Desk', quantity: 3 },
      { name: 'Bookshelf', quantity: 2 },
      { name: 'Large Box', quantity: 20 },
      { name: 'Medium Box', quantity: 45 },
      { name: 'Small Box', quantity: 25 },
    ],
  };

  const handleQuickPreset = async (presetKey) => {
    if (!jobId) {
      toast.error('Please select a job first');
      return;
    }

    const preset = presets[presetKey];
    if (!preset) return;

    try {
      toast.info(`Adding ${presetKey} preset items...`);
      
      for (const item of preset) {
        // Find the template for this item
        const template = templates.find(t => 
          t.name.toLowerCase() === item.name.toLowerCase()
        );
        
        if (template) {
          await api.post('/inventory', {
            job_id: jobId,
            category: template.category,
            item_name: template.name,
            quantity: item.quantity,
            is_fragile: template.is_fragile,
            notes: `Added via ${presetKey} preset`
          });
        }
      }
      
      // Refresh inventory list
      const itemsRes = await api.get(`/jobs/${jobId}/inventory`);
      setInventoryItems(itemsRes.data);
      
      toast.success(`${presetKey} preset items added!`);
    } catch (error) {
      toast.error('Failed to add preset items');
    }
  };

  const getCategoryIcon = (category) => {
    const Icon = categoryIcons[category] || categoryIcons.default;
    return <Icon size={16} />;
  };

  // If no job selected, show job selector
  if (!jobId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="inventory-manager">
        <header className="bg-white border-b shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-xl font-bold">Inventory Manager</h1>
                <p className="text-sm text-gray-500">Select a job to manage inventory</p>
              </div>
            </div>
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase size={20} />
                Select a Job
              </CardTitle>
              <CardDescription>
                Choose a job to add or manage inventory items
              </CardDescription>
            </CardHeader>
            <CardContent>
              {jobsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Package className="animate-pulse text-primary mr-2" size={24} />
                  <span>Loading jobs...</span>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8">
                  <Package size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 mb-4">No jobs found</p>
                  <Button onClick={() => navigate('/control-panel/jobs/create')}>
                    <Plus size={16} className="mr-2" />
                    Create a Job First
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleSelectJob(job.id)}
                      data-testid={`select-job-${job.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{job.job_number || job.id}</p>
                          <p className="text-sm text-gray-500">
                            {job.from_address?.city || 'N/A'} → {job.to_address?.city || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Status: {job.status?.replace('_', ' ')}
                          </p>
                        </div>
                        <Badge variant="outline">{job.customer_type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="inventory-manager">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-xl font-bold">Inventory Manager</h1>
                <p className="text-sm text-gray-500">Add items from pre-made list</p>
              </div>
            </div>
            {jobId && (
              <Badge variant="outline" className="text-sm">
                {inventoryItems.length} items added
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Item Templates */}
          <div className="lg:col-span-2 space-y-4">
            {/* Type Tabs */}
            <Tabs value={inventoryType} onValueChange={setInventoryType}>
              <TabsList className="w-full">
                <TabsTrigger value="domestic" className="flex-1">Domestic</TabsTrigger>
                <TabsTrigger value="commercial" className="flex-1">Commercial</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search and Filter */}
            <Card>
              <CardContent className="pt-4 space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="inventory-search"
                  />
                </div>
                
                {/* Horizontal Scrolling Categories */}
                <div className="overflow-x-auto pb-2 -mx-4 px-4">
                  <div className="flex gap-2 min-w-max">
                    <Button
                      variant={selectedCategory === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory('all')}
                      className="whitespace-nowrap"
                    >
                      All
                    </Button>
                    {categories[inventoryType]?.map(cat => (
                      <Button
                        key={cat}
                        variant={selectedCategory === cat ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory(cat)}
                        className="whitespace-nowrap flex items-center gap-1"
                      >
                        {getCategoryIcon(cat)}
                        {cat}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Quick Add Presets */}
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-2">Quick Add Presets:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickPreset('1-bed')}
                      className="text-xs"
                    >
                      🏠 1 Bedroom
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickPreset('2-bed')}
                      className="text-xs"
                    >
                      🏠 2 Bedroom
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickPreset('3-bed')}
                      className="text-xs"
                    >
                      🏡 3 Bedroom
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickPreset('4-bed')}
                      className="text-xs"
                    >
                      🏡 4 Bedroom
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleQuickPreset('5-bed')}
                      className="text-xs"
                    >
                      🏰 5 Bedroom
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Item Grid */}
            {loading ? (
              <div className="text-center py-12">Loading inventory templates...</div>
            ) : (
              <ScrollArea className="h-[calc(100vh-280px)]">
                <div className="space-y-6 pr-4">
                  {Object.entries(groupedTemplates).map(([category, items]) => (
                    <div key={category}>
                      <div className="flex items-center gap-2 mb-3">
                        {getCategoryIcon(category)}
                        <h3 className="font-semibold text-gray-700">{category}</h3>
                        <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {items.map((item, idx) => (
                          <Card 
                            key={`${item.name}-${idx}`}
                            className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-primary/50"
                            onClick={() => openAddDialog(item)}
                            data-testid={`item-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-medium text-sm">{item.name}</h4>
                                  <p className="text-xs text-gray-500 mt-1">{item.dimensions}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {item.is_fragile && (
                                    <Badge variant="destructive" className="text-xs">
                                      <AlertTriangle size={10} className="mr-1" />
                                      Fragile
                                    </Badge>
                                  )}
                                  <Plus size={18} className="text-primary" />
                                </div>
                              </div>
                              {item.disassemble_time && (
                                <p className="text-xs text-orange-600 mt-2">
                                  Assembly: {item.disassemble_time}min / {item.reassemble_time}min
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Right: Current Inventory */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package size={20} />
                  Current Inventory
                </CardTitle>
                <CardDescription>
                  Items added to this job
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!jobId ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Select a job to manage inventory</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/control-panel/jobs')}>
                      View Jobs
                    </Button>
                  </div>
                ) : inventoryItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No items added yet</p>
                    <p className="text-xs mt-2">Click items on the left to add them</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 pr-4">
                      {inventoryItems.map((item) => (
                        <div 
                          key={item.id}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                          data-testid={`inventory-item-${item.id}`}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.item_name}</p>
                            <p className="text-xs text-gray-500">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{item.quantity}x</Badge>
                            {item.is_fragile && (
                              <AlertTriangle size={14} className="text-red-500" />
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              <Trash2 size={14} className="text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats */}
            {inventoryItems.length > 0 && (
              <Card>
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-primary">
                        {inventoryItems.reduce((sum, i) => sum + i.quantity, 0)}
                      </p>
                      <p className="text-xs text-gray-500">Total Items</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-500">
                        {inventoryItems.filter(i => i.is_fragile).length}
                      </p>
                      <p className="text-xs text-gray-500">Fragile Items</p>
                    </div>
                  </div>
                  
                  {/* Create Estimate Button */}
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      if (jobId) {
                        navigate(`/control-panel/jobs/${jobId}?tab=estimates`);
                      } else {
                        toast.error('Please select a job first');
                      }
                    }}
                    data-testid="create-estimate-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    Create Estimate
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Item</DialogTitle>
            <DialogDescription>
              Add {selectedItem?.name} to your inventory
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold">{selectedItem.name}</h3>
                <p className="text-sm text-gray-500">{selectedItem.category}</p>
                <p className="text-xs text-gray-400 mt-1">Dimensions: {selectedItem.dimensions}</p>
                
                {selectedItem.is_fragile && (
                  <Badge variant="destructive" className="mt-2">
                    <AlertTriangle size={12} className="mr-1" />
                    Fragile Item
                  </Badge>
                )}
                
                {selectedItem.notes && (
                  <p className="text-xs text-orange-600 mt-2">{selectedItem.notes}</p>
                )}
              </div>

              <div>
                <Label>Quantity</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 text-center"
                    data-testid="quantity-input"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </Button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddDialog(false)}>
                  <X size={16} className="mr-2" />
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleAddItem} data-testid="add-item-confirm">
                  <Check size={16} className="mr-2" />
                  Add {quantity} Item{quantity > 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryManager;
